/**
 * Notification Settings Screen
 * Allows familie admins to customize notification appearance for different types
 */

import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Switch,
  Modal,
  Dimensions,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { LinearGradient } from "expo-linear-gradient";
import LottieView from "lottie-react-native";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import {
  NOTIFICATION_THEMES,
  getLottieAnimationsList,
  getLottieAnimation,
  DEFAULT_THEME_BY_TYPE,
  DEFAULT_LOTTIE_BY_TYPE,
  NotificationTheme,
  LottieAnimationInfo,
} from "@/modules/notifications";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

/**
 * Notification type config
 */
interface NotificationTypeConfig {
  id: string;
  label: string;
  description: string;
  icon: string;
  themeId: string;
  lottieAnimation: string;
  isEnabled: boolean;
  sendPush: boolean;
}

const DEFAULT_NOTIFICATION_TYPES: NotificationTypeConfig[] = [
  {
    id: "score_positive",
    label: "Punten Ontvangen",
    description: "Wanneer iemand punten verdient",
    icon: "trophy-outline",
    themeId: DEFAULT_THEME_BY_TYPE.score_positive || "purple-orange",
    lottieAnimation: DEFAULT_LOTTIE_BY_TYPE.score_positive || "In_Clouds_Emoji",
    isEnabled: true,
    sendPush: true,
  },
  {
    id: "score_negative",
    label: "Punten Afgetrokken",
    description: "Wanneer punten worden afgetrokken",
    icon: "trending-down-outline",
    themeId: DEFAULT_THEME_BY_TYPE.score_negative || "orange-purple",
    lottieAnimation: DEFAULT_LOTTIE_BY_TYPE.score_negative || "Loudly_Crying_Emoji",
    isEnabled: true,
    sendPush: true,
  },
  {
    id: "event",
    label: "Agenda Notificaties",
    description: "Nieuwe of gewijzigde agenda items",
    icon: "calendar-outline",
    themeId: DEFAULT_THEME_BY_TYPE.event,
    lottieAnimation: DEFAULT_LOTTIE_BY_TYPE.event,
    isEnabled: true,
    sendPush: true,
  },
  {
    id: "challenge",
    label: "Challenge Notificaties",
    description: "Nieuwe challenges die live gaan",
    icon: "game-controller-outline",
    themeId: DEFAULT_THEME_BY_TYPE.challenge,
    lottieAnimation: DEFAULT_LOTTIE_BY_TYPE.challenge,
    isEnabled: true,
    sendPush: true,
  },
  {
    id: "birthday",
    label: "Verjaardag Notificaties",
    description: "Wanneer iemand jarig is",
    icon: "gift-outline",
    themeId: DEFAULT_THEME_BY_TYPE.birthday,
    lottieAnimation: "cake",
    isEnabled: true,
    sendPush: true,
  },
];

/**
 * Notification Settings Screen Component
 */
export default function NotificationSettingsScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { activeWeekend } = useAuth();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState<NotificationTypeConfig[]>(DEFAULT_NOTIFICATION_TYPES);
  const [hasChanges, setHasChanges] = useState(false);

  // Modal state
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [showLottieModal, setShowLottieModal] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);

  /**
   * Fetch notification settings from database
   */
  const fetchSettings = useCallback(async () => {
    if (!activeWeekend?.id) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("notification_type_settings")
        .select("*")
        .eq("weekend_id", activeWeekend.id);

      if (error) {
        console.error("Error fetching notification settings:", error);
        setIsLoading(false);
        return;
      }

      if (data && data.length > 0) {
        // Merge database settings with defaults
        const updatedTypes = DEFAULT_NOTIFICATION_TYPES.map(defaultType => {
          const dbSetting = data.find(s => s.notification_type === defaultType.id);
          if (dbSetting) {
            return {
              ...defaultType,
              themeId: dbSetting.theme_id || defaultType.themeId,
              lottieAnimation: dbSetting.lottie_animation || defaultType.lottieAnimation,
              isEnabled: dbSetting.is_enabled,
              sendPush: dbSetting.send_push,
            };
          }
          return defaultType;
        });
        setNotificationTypes(updatedTypes);
      }
    } catch (err) {
      console.error("Error in fetchSettings:", err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [activeWeekend?.id]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    fetchSettings();
  }, [fetchSettings]);

  /**
   * Save settings to database
   */
  const handleSave = async () => {
    if (!activeWeekend?.id) return;

    setIsSaving(true);
    try {
      // Upsert each notification type setting
      for (const type of notificationTypes) {
        const { error } = await supabase
          .from("notification_type_settings")
          .upsert(
            {
              weekend_id: activeWeekend.id,
              notification_type: type.id,
              theme_id: type.themeId,
              lottie_animation: type.lottieAnimation,
              is_enabled: type.isEnabled,
              send_push: type.sendPush,
              updated_at: new Date().toISOString(),
            },
            {
              onConflict: "weekend_id,notification_type",
            }
          );

        if (error) {
          console.error("Error saving notification setting:", error);
          throw error;
        }
      }

      setHasChanges(false);
    } catch (err) {
      console.error("Error saving settings:", err);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Update a notification type setting
   */
  const updateTypeSetting = (
    typeId: string,
    updates: Partial<NotificationTypeConfig>
  ) => {
    setNotificationTypes(prev =>
      prev.map(t => (t.id === typeId ? { ...t, ...updates } : t))
    );
    setHasChanges(true);
  };

  /**
   * Open theme selector for a type
   */
  const openThemeSelector = (typeId: string) => {
    setEditingTypeId(typeId);
    setShowThemeModal(true);
  };

  /**
   * Open lottie selector for a type
   */
  const openLottieSelector = (typeId: string) => {
    setEditingTypeId(typeId);
    setShowLottieModal(true);
  };

  /**
   * Select a theme
   */
  const selectTheme = (themeId: string) => {
    if (editingTypeId) {
      updateTypeSetting(editingTypeId, { themeId });
    }
    setShowThemeModal(false);
    setEditingTypeId(null);
  };

  /**
   * Select a lottie animation
   */
  const selectLottie = (lottieId: string) => {
    if (editingTypeId) {
      updateTypeSetting(editingTypeId, { lottieAnimation: lottieId });
    }
    setShowLottieModal(false);
    setEditingTypeId(null);
  };

  /**
   * Get theme by ID
   */
  const getTheme = (themeId: string): NotificationTheme => {
    return NOTIFICATION_THEMES.find(t => t.id === themeId) || NOTIFICATION_THEMES[0];
  };

  /**
   * Render a notification type card
   */
  const renderNotificationType = (type: NotificationTypeConfig) => {
    const theme = getTheme(type.themeId);

    return (
      <View
        key={type.id}
        className="rounded-2xl overflow-hidden mb-4"
        style={{
          backgroundColor: colors.surface,
          borderWidth: 1,
          borderColor: colors.border,
        }}
      >
        {/* Header */}
        <View className="flex-row items-center p-4" style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}>
          <View
            className="w-10 h-10 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: colors.primary + "20" }}
          >
            <Ionicons name={type.icon as any} size={20} color={colors.primary} />
          </View>
          <View className="flex-1">
            <Text className="font-semibold" style={{ color: colors.text }}>
              {type.label}
            </Text>
            <Text className="text-xs" style={{ color: colors.textSecondary }}>
              {type.description}
            </Text>
          </View>
          <Switch
            value={type.isEnabled}
            onValueChange={(value) => updateTypeSetting(type.id, { isEnabled: value })}
            trackColor={{ false: colors.border, true: colors.primary + "60" }}
            thumbColor={type.isEnabled ? colors.primary : colors.textTertiary}
          />
        </View>

        {/* Settings */}
        {type.isEnabled && (
          <View className="p-4">
            {/* Theme Preview */}
            <TouchableOpacity
              onPress={() => openThemeSelector(type.id)}
              className="flex-row items-center mb-4"
              accessibilityLabel="Kies achtergrond thema"
            >
              <View className="flex-1">
                <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                  Achtergrond
                </Text>
                <Text className="text-xs" style={{ color: colors.textSecondary }}>
                  {theme.name}
                </Text>
              </View>
              <View className="rounded-xl overflow-hidden" style={{ width: 60, height: 40 }}>
                <LinearGradient
                  colors={[theme.gradient_start, theme.gradient_middle || theme.gradient_end, theme.gradient_end]}
                  style={{ flex: 1 }}
                />
              </View>
              <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} style={{ marginLeft: 8 }} />
            </TouchableOpacity>

            {/* Lottie Preview */}
            <TouchableOpacity
              onPress={() => openLottieSelector(type.id)}
              className="flex-row items-center mb-4"
              accessibilityLabel="Kies animatie"
            >
              <View className="flex-1">
                <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                  Animatie
                </Text>
                <Text className="text-xs" style={{ color: colors.textSecondary }}>
                  {getLottieAnimationsList().find(l => l.id === type.lottieAnimation)?.name || type.lottieAnimation}
                </Text>
              </View>
              <View className="rounded-xl overflow-hidden" style={{ width: 48, height: 48, backgroundColor: colors.surfaceSecondary }}>
                <LottieView
                  source={getLottieAnimation(type.lottieAnimation)}
                  style={{ width: 48, height: 48 }}
                  autoPlay
                  loop
                />
              </View>
              <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} style={{ marginLeft: 8 }} />
            </TouchableOpacity>

            {/* Push notification toggle */}
            <View className="flex-row items-center justify-between">
              <View>
                <Text className="text-sm font-medium" style={{ color: colors.text }}>
                  Push Notificatie
                </Text>
                <Text className="text-xs" style={{ color: colors.textSecondary }}>
                  Ook sturen als app niet open is
                </Text>
              </View>
              <Switch
                value={type.sendPush}
                onValueChange={(value) => updateTypeSetting(type.id, { sendPush: value })}
                trackColor={{ false: colors.border, true: colors.primary + "60" }}
                thumbColor={type.sendPush ? colors.primary : colors.textTertiary}
              />
            </View>
          </View>
        )}
      </View>
    );
  };

  /**
   * Render theme selection modal
   */
  const renderThemeModal = () => (
    <Modal
      visible={showThemeModal}
      animationType="slide"
      transparent
      onRequestClose={() => setShowThemeModal(false)}
    >
      <View className="flex-1 justify-end" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
        <View
          className="rounded-t-3xl p-6"
          style={{ backgroundColor: colors.surface, maxHeight: "80%" }}
        >
          <View className="flex-row justify-between items-center mb-4">
            <Text className="text-xl font-bold" style={{ color: colors.text }}>
              Kies Achtergrond
            </Text>
            <TouchableOpacity onPress={() => setShowThemeModal(false)}>
              <Ionicons name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>
            <View className="flex-row flex-wrap justify-between">
              {NOTIFICATION_THEMES.map((theme) => (
                <TouchableOpacity
                  key={theme.id}
                  onPress={() => selectTheme(theme.id)}
                  className="mb-4 rounded-2xl overflow-hidden"
                  style={{
                    width: (SCREEN_WIDTH - 60) / 2,
                    height: 120,
                    borderWidth: editingTypeId && notificationTypes.find(t => t.id === editingTypeId)?.themeId === theme.id ? 3 : 0,
                    borderColor: colors.primary,
                  }}
                >
                  <LinearGradient
                    colors={[theme.gradient_start, theme.gradient_middle || theme.gradient_end, theme.gradient_end]}
                    style={{ flex: 1, justifyContent: "flex-end", padding: 12 }}
                  >
                    <Text className="font-semibold" style={{ color: "white" }}>
                      {theme.name}
                    </Text>
                  </LinearGradient>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  /**
   * Render lottie selection modal
   */
  const renderLottieModal = () => {
    const animations = getLottieAnimationsList();

    return (
      <Modal
        visible={showLottieModal}
        animationType="slide"
        transparent
        onRequestClose={() => setShowLottieModal(false)}
      >
        <View className="flex-1 justify-end" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <View
            className="rounded-t-3xl p-6"
            style={{ backgroundColor: colors.surface, maxHeight: "80%" }}
          >
            <View className="flex-row justify-between items-center mb-4">
              <Text className="text-xl font-bold" style={{ color: colors.text }}>
                Kies Animatie
              </Text>
              <TouchableOpacity onPress={() => setShowLottieModal(false)}>
                <Ionicons name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>

            <ScrollView showsVerticalScrollIndicator={false}>
              <View className="flex-row flex-wrap justify-between">
                {animations.map((anim) => (
                  <TouchableOpacity
                    key={anim.id}
                    onPress={() => selectLottie(anim.id)}
                    className="mb-4 rounded-2xl items-center justify-center p-2"
                    style={{
                      width: (SCREEN_WIDTH - 72) / 3,
                      height: 100,
                      backgroundColor: colors.surfaceSecondary,
                      borderWidth: editingTypeId && notificationTypes.find(t => t.id === editingTypeId)?.lottieAnimation === anim.id ? 3 : 0,
                      borderColor: colors.primary,
                    }}
                  >
                    <LottieView
                      source={anim.file}
                      style={{ width: 56, height: 56 }}
                      autoPlay
                      loop
                    />
                    <Text
                      className="text-xs text-center mt-1"
                      style={{ color: colors.textSecondary }}
                      numberOfLines={1}
                    >
                      {anim.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
      </SafeAreaView>
    );
  }

  if (!activeWeekend) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center px-6"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <Ionicons name="calendar-outline" size={64} color={colors.textTertiary} />
        <Text className="text-lg font-medium mt-4 text-center" style={{ color: colors.text }}>
          Geen actief weekend
        </Text>
        <Text className="mt-2 text-center" style={{ color: colors.textSecondary }}>
          Er moet eerst een weekend actief zijn om notificatie-instellingen aan te passen.
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      <ScrollView
        className="flex-1 px-4"
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Header info */}
        <View
          className="rounded-2xl p-4 mb-6 flex-row items-start"
          style={{ backgroundColor: colors.primary + "10" }}
        >
          <Ionicons name="information-circle" size={24} color={colors.primary} />
          <Text className="flex-1 ml-3 text-sm" style={{ color: colors.text }}>
            Pas hier aan hoe notificaties eruit zien voor de deelnemers van dit familie weekend.
          </Text>
        </View>

        {/* Notification Types */}
        {notificationTypes.map(renderNotificationType)}

        {/* Save button */}
        {hasChanges && (
          <TouchableOpacity
            onPress={handleSave}
            disabled={isSaving}
            className="rounded-xl p-4 mb-8 flex-row items-center justify-center"
            style={{ backgroundColor: colors.primary }}
          >
            {isSaving ? (
              <ActivityIndicator size="small" color="white" />
            ) : (
              <>
                <Ionicons name="save-outline" size={20} color="white" />
                <Text className="ml-2 font-semibold" style={{ color: "white" }}>
                  Instellingen Opslaan
                </Text>
              </>
            )}
          </TouchableOpacity>
        )}

        <View className="h-8" />
      </ScrollView>

      {/* Modals */}
      {renderThemeModal()}
      {renderLottieModal()}
    </SafeAreaView>
  );
}


